import { Article } from '../../components/Layout'
import {
  H1, H2, H3, P, Lead, UL, OL, LI, Code, CodeBlock,
  Callout, Steps, Step, PropTable, Tag, Grid, Card, Divider,
} from '../../components/DocComponents'

const toc = [
  { id: 'overview',         title: 'Overview',                  level: 2 as const },
  { id: 'system-templates', title: 'System templates',          level: 2 as const },
  { id: 'create-template',  title: 'Creating a template',       level: 2 as const },
  { id: 'editor',           title: 'Template editor',           level: 2 as const },
  { id: 'section-types',    title: 'Section types',             level: 2 as const },
  { id: 'html-override',    title: 'HTML override mode',        level: 2 as const },
  { id: 'generate-api',     title: 'Generating a PDF via API',  level: 2 as const },
  { id: 'async-jobs',       title: 'Async jobs',                level: 2 as const },
  { id: 'payload-reference','title': 'Payload reference',       level: 2 as const },
]

export function PdfTemplates() {
  return (
    <Article toc={toc}>
      <H1>PDF Templates &amp; Generation</H1>
      <Lead>
        The PDF composition feature lets you design document templates visually, then generate
        PDFs on demand — either synchronously (receive the file immediately) or asynchronously
        (queue a job and receive a Buzz event when it is ready).
      </Lead>

      <H2 id="overview">Overview</H2>
      <P>
        A <strong>PDF Template</strong> defines the structure, branding, and layout of a document.
        Templates are made up of a header block, typed sections (tables, summaries, text fields,
        signature blocks…), and a footer. Each variable inside a template maps to a key in the
        <strong> payload</strong> you send when generating the PDF.
      </P>
      <Grid cols={3}>
        <Card title="Design" icon="🎨">
          Use the visual editor to configure colours, fonts, header fields, and body sections. No
          HTML required.
        </Card>
        <Card title="Generate" icon="⚡">
          POST a payload to the generate endpoint and receive a document URL in milliseconds (sync)
          or as a background job (async).
        </Card>
        <Card title="Store" icon="🗄️">
          Generated PDFs are saved automatically to a Dataset and can be retrieved through the
          standard Documents API.
        </Card>
      </Grid>

      <H2 id="system-templates">System templates</H2>
      <P>
        Rabbit ships five read-only system templates that cover common document types:
      </P>
      <UL>
        <LI><strong>Invoice</strong> — header with invoice number &amp; date, line-items table, subtotal/tax/total summary</LI>
        <LI><strong>Contract</strong> — title, parties block, multi-section body, signature block</LI>
        <LI><strong>Letter</strong> — letterhead, recipient address, free-text body</LI>
        <LI><strong>Purchase Order</strong> — PO number, vendor block, items table, totals</LI>
        <LI><strong>Shipping Label</strong> — sender/recipient addresses, tracking and weight fields</LI>
      </UL>
      <Callout type="info">
        System templates are <strong>read-only</strong>. Click the <strong>Clone</strong> icon on a
        system template card to create your own editable copy. Cloned templates inherit all
        sections and branding, which you can then customise freely.
      </Callout>

      <H2 id="create-template">Creating a template</H2>
      <Steps>
        <Step n={1} title="Open PDF Templates">
          Navigate to <strong>Storage › PDF Templates</strong> in the console sidebar.
        </Step>
        <Step n={2} title="Choose a starting point">
          <P>Two options:</P>
          <UL>
            <LI><strong>Clone a system template</strong> — click the copy icon on any card in the
            "System templates" section. The clone opens immediately in the editor.</LI>
            <LI><strong>New blank template</strong> — click <strong>New template</strong> in the
            top-right, enter a name, optional description, and document type, then click
            <strong> Create</strong>.</LI>
          </UL>
        </Step>
        <Step n={3} title="Configure &amp; preview">
          The editor opens in split-view. Use the left panel tabs to configure the template and
          click <strong>Refresh</strong> in the right panel to see a live HTML preview.
        </Step>
        <Step n={4} title="Save">
          Click <strong>Save</strong> in the header bar. Unsaved changes are not reflected in the
          preview or in generated PDFs.
        </Step>
      </Steps>

      <H2 id="editor">Template editor</H2>
      <P>
        The editor is split into a <strong>config panel</strong> (left, ~40% width) and a
        <strong> preview panel</strong> (right). The config panel has five tabs:
      </P>

      <H3>Branding</H3>
      <UL>
        <LI><strong>Primary colour</strong> — used for the header background, table headings, and
        accent lines. Accepts any CSS hex value.</LI>
        <LI><strong>Secondary / accent colour</strong> — used for alternating table row stripes and
        subtle backgrounds.</LI>
        <LI><strong>Font family</strong> — choose from Arial, Georgia, Courier New, Verdana, or
        Trebuchet MS.</LI>
        <LI><strong>Logo URL or base64</strong> — paste an HTTPS image URL or a
        <Code>data:image/png;base64,…</Code> string. The logo appears top-left in the header.</LI>
      </UL>

      <H3>Header</H3>
      <P>
        The document header renders as a two-column block: business info on the left, document
        title and fields on the right.
      </P>
      <UL>
        <LI><strong>Business name / address / contact</strong> — static strings baked into the
        template.</LI>
        <LI><strong>Document title</strong> — e.g. <Code>INVOICE</Code>, <Code>CONTRACT</Code>. Large
        text in the top-right of the header.</LI>
        <LI><strong>Header fields</strong> — dynamic label/value pairs (e.g. "Invoice #" →
        <Code>invoiceNumber</Code>). Each field maps a display label to a payload variable name. Add
        as many as needed with the <strong>Add field</strong> button.</LI>
        <LI><strong>Recipient block</strong> — optional "Bill To" / "Ship To" style address block.
        Enable the toggle and set the label and payload variable name.</LI>
      </UL>

      <H3>Sections</H3>
      <P>
        Sections form the document body between the header and footer. They are rendered in order.
        Each section type is described in the <a href="#section-types">Section types</a> section
        below.
      </P>

      <H3>Footer</H3>
      <UL>
        <LI><strong>Footer text</strong> — static string shown centred at the bottom of every
        page.</LI>
        <LI><strong>Show page numbers</strong> — appends "Page N of M" rendered by Puppeteer's
        native footer template.</LI>
      </UL>

      <H3>Page</H3>
      <UL>
        <LI><strong>Page size</strong> — A4, Letter, or Legal.</LI>
        <LI><strong>Orientation</strong> — Portrait or Landscape.</LI>
        <LI><strong>Margins</strong> — individual top / right / bottom / left values in mm.</LI>
      </UL>

      <Callout type="tip">
        Click <strong>Refresh</strong> after saving to load the latest HTML into the preview iframe.
        The preview renders the same HTML that Puppeteer will convert to PDF, so what you see is
        what you get.
      </Callout>

      <H2 id="section-types">Section types</H2>
      <P>
        Each section type has one or more <strong>payload variables</strong> — keys your API caller
        must supply in the <Code>payload</Code> object when generating the PDF.
      </P>

      <H3>Text</H3>
      <P>Renders a labelled paragraph or heading from a single string variable.</P>
      <PropTable props={[
        { name: 'label',    type: 'string',  required: true,  description: 'Section heading shown above the text.' },
        { name: 'variable', type: 'string',  required: true,  description: 'Payload key whose value is rendered as the text.' },
        { name: 'multiline',type: 'boolean', default: 'false', description: 'If true, newlines in the value are preserved.' },
        { name: 'style',    type: 'normal | bold | heading', default: 'normal', description: 'Visual weight of the rendered text.' },
      ]} />

      <H3>Table</H3>
      <P>Renders a data table from an array variable. Each column maps a key in the row objects.</P>
      <PropTable props={[
        { name: 'label',    type: 'string',  required: true,  description: 'Section heading above the table.' },
        { name: 'variable', type: 'string',  required: true,  description: 'Payload key whose value is an array of row objects.' },
        { name: 'columns',  type: 'Column[]',required: true,  description: 'Column definitions: key, label, width (%), align, format.' },
        { name: 'stripe',   type: 'boolean', default: 'true', description: 'Alternate row background using the secondary colour.' },
      ]} />
      <P>Column <Code>format</Code> values:</P>
      <UL>
        <LI><Code>text</Code> — rendered as-is</LI>
        <LI><Code>number</Code> — right-aligned, formatted with <Code>toLocaleString()</Code></LI>
        <LI><Code>currency</Code> — right-aligned with two decimal places and currency symbol</LI>
        <LI><Code>date</Code> — formatted as locale date string</LI>
      </UL>

      <H3>Summary</H3>
      <P>Renders a right-aligned (or left-aligned) two-column list of label/value pairs — ideal
      for subtotals, taxes, and totals at the bottom of an invoice.</P>
      <PropTable props={[
        { name: 'rows',    type: 'Row[]',  required: true,  description: 'Each row: label (string), variable (payload key), format, bold, separator.' },
        { name: 'align',   type: 'left | right', default: 'right', description: 'Horizontal alignment of the summary block.' },
      ]} />

      <H3>Signature</H3>
      <P>Renders one or more signature blocks with a name line, date line, and signature rule.</P>
      <PropTable props={[
        { name: 'blocks',  type: 'Block[]', required: true, description: 'Each block: label, nameVar (payload key for signer name), dateVar (payload key for date).' },
      ]} />

      <H3>Divider &amp; Spacer</H3>
      <P>
        <strong>Divider</strong> renders a horizontal rule. <strong>Spacer</strong> inserts an
        empty block of configurable height (px) — useful for breathing room between sections.
        Neither type reads from the payload.
      </P>

      <H2 id="html-override">HTML override mode</H2>
      <P>
        For full control over layout, switch the preview panel to <strong>HTML override</strong>
        mode. In this mode the config-generated HTML is replaced entirely by your own HTML string,
        which is processed with <strong>Handlebars</strong> before being passed to Puppeteer.
      </P>
      <Callout type="warning">
        Enabling HTML override disables the visual config. You are responsible for all layout,
        styles, and variable interpolation. Inline styles are required — external CSS files are
        not loaded by Puppeteer in this context.
      </Callout>
      <CodeBlock language="html" title="HTML override with Handlebars syntax">
{`<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; margin: 30px; color: #111; }
    h1   { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; }
    th { background: #1e40af; color: white; }
  </style>
</head>
<body>
  <h1>INVOICE #{{invoiceNumber}}</h1>
  <p>Date: {{date}}</p>
  <p>Bill To: {{recipient}}</p>

  <table>
    <thead>
      <tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
    </thead>
    <tbody>
      {{#each items}}
      <tr>
        <td>{{description}}</td>
        <td>{{qty}}</td>
        <td>{{unitPrice}}</td>
        <td>{{total}}</td>
      </tr>
      {{/each}}
    </tbody>
  </table>

  <p style="text-align:right; font-weight:bold;">Grand Total: {{grandTotal}}</p>
</body>
</html>`}
      </CodeBlock>
      <P>Supported Handlebars syntax:</P>
      <UL>
        <LI><Code>{'{{variable}}'}</Code> — interpolate a string or number</LI>
        <LI><Code>{'{{#each array}}…{{/each}}'}</Code> — iterate over an array</LI>
        <LI><Code>{'{{#if condition}}…{{/if}}'}</Code> — conditional block</LI>
      </UL>

      <Divider />

      <H2 id="generate-api">Generating a PDF via API</H2>
      <P>
        Use the generate endpoint to produce a PDF from any template. You must supply the
        <Code>templateId</Code> and a <Code>payload</Code> object whose keys match the variable
        names defined in the template.
      </P>

      <H3>Endpoint</H3>
      <CodeBlock language="bash">
{`POST /api/pdf/generate
Authorization: Bearer <token>
Content-Type: application/json`}
      </CodeBlock>

      <H3>Request body</H3>
      <PropTable props={[
        { name: 'templateId', type: 'string (UUID)', required: true,  description: 'ID of the PDF template to use.' },
        { name: 'payload',    type: 'object',        default: '{}',   description: 'Key/value data passed into the template. Keys must match the variable names configured in the template sections.' },
        { name: 'mode',       type: 'sync | async',  default: 'sync', description: '"sync" waits for the PDF and returns the document URL. "async" queues a background job and returns immediately.' },
        { name: 'datasetId',  type: 'string (UUID)', description: 'Dataset to save the generated document into. Defaults to the auto-created "Generated PDFs" dataset for the business.' },
      ]} />

      <H3>Sync example — Invoice</H3>
      <CodeBlock language="bash" title="cURL">
{`curl -X POST http://localhost:7000/api/pdf/generate \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "templateId": "tpl_invoice_id_here",
    "mode": "sync",
    "payload": {
      "invoiceNumber": "INV-2024-0042",
      "date": "2024-06-01",
      "dueDate": "2024-06-30",
      "recipient": "Acme Corp\\n12 Market St\\nSan Francisco, CA 94105",
      "items": [
        { "description": "Consulting (10 hrs)", "qty": 10, "unitPrice": 150, "total": 1500 },
        { "description": "Hosting — June",      "qty":  1, "unitPrice":  49, "total":   49 }
      ],
      "subtotal": 1549,
      "tax":       154.90,
      "total":    1703.90
    }
  }'`}
      </CodeBlock>
      <CodeBlock language="json" title="Sync response">
{`{
  "jobId":       "a3f7c812-...",
  "documentId":  "b1e2d934-...",
  "documentUrl": "http://localhost:7000/api/documents/b1e2d934-.../download",
  "status":      "completed"
}`}
      </CodeBlock>
      <P>
        The <Code>documentUrl</Code> is a direct download link. It requires the same
        <Code>Authorization</Code> header as all other storage endpoints.
      </P>

      <H3>Sync example — Contract</H3>
      <CodeBlock language="json" title="Contract payload">
{`{
  "templateId": "tpl_contract_id_here",
  "mode": "sync",
  "payload": {
    "contractNumber": "CTR-2024-007",
    "effectiveDate":  "2024-07-01",
    "partyA": "Rabbit Technologies Inc.",
    "partyB": "Acme Corp",
    "scope":  "Software development services as described in Exhibit A.",
    "terms":  "Payment due within 30 days of invoice date.",
    "signerAName": "Alice Smith",
    "signerADate": "2024-07-01",
    "signerBName": "Bob Jones",
    "signerBDate": "2024-07-01"
  }
}`}
      </CodeBlock>

      <H3>Sync example — Shipping Label</H3>
      <CodeBlock language="json" title="Shipping label payload">
{`{
  "templateId": "tpl_shipping_id_here",
  "mode": "sync",
  "payload": {
    "trackingNumber": "1Z999AA10123456784",
    "carrier":        "UPS Ground",
    "shipDate":       "2024-06-15",
    "weight":         "2.4 kg",
    "senderName":     "Rabbit Technologies Inc.",
    "senderAddress":  "1 Main Street, New York, NY 10001",
    "recipientName":  "Acme Corp",
    "recipientAddress": "12 Market St, San Francisco, CA 94105"
  }
}`}
      </CodeBlock>

      <H2 id="async-jobs">Async jobs</H2>
      <P>
        Set <Code>"mode": "async"</Code> to queue a background job. The endpoint returns
        immediately with a <Code>jobId</Code>. The worker processes up to 3 pending jobs every 5
        seconds and emits a <Code>pdf.generated</Code> Buzz event when complete.
      </P>

      <H3>Async request</H3>
      <CodeBlock language="bash" title="cURL — async">
{`curl -X POST http://localhost:7000/api/pdf/generate \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "templateId": "tpl_invoice_id_here",
    "mode": "async",
    "datasetId": "ds_invoices_id_here",
    "payload": { ... }
  }'`}
      </CodeBlock>
      <CodeBlock language="json" title="Async response (202 Accepted)">
{`{
  "jobId":  "a3f7c812-...",
  "status": "pending"
}`}
      </CodeBlock>

      <H3>Polling job status</H3>
      <CodeBlock language="bash">
{`GET /api/pdf/jobs/{jobId}
Authorization: Bearer <token>`}
      </CodeBlock>
      <CodeBlock language="json" title="Completed job">
{`{
  "id":          "a3f7c812-...",
  "status":      "completed",
  "documentId":  "b1e2d934-...",
  "documentUrl": "http://localhost:7000/api/documents/b1e2d934-.../download",
  "processedAt": "2024-06-01T12:00:05.123Z"
}`}
      </CodeBlock>
      <P>Possible <Code>status</Code> values:</P>
      <UL>
        <LI><Tag color="amber">pending</Tag> — queued, not yet picked up by the worker</LI>
        <LI><Tag color="blue">processing</Tag> — Puppeteer is rendering the PDF</LI>
        <LI><Tag color="green">completed</Tag> — PDF saved; <Code>documentUrl</Code> is available</LI>
        <LI><Tag color="red">failed</Tag> — generation failed; <Code>error</Code> field contains the reason</LI>
      </UL>

      <H3>Buzz event</H3>
      <P>
        When an async job completes, storage-service emits a <Code>pdf.generated</Code> event to
        the Buzz service. Subscribe to this event to get notified without polling.
      </P>
      <CodeBlock language="json" title="pdf.generated event payload">
{`{
  "event":       "pdf.generated",
  "businessId":  "biz_...",
  "jobId":       "a3f7c812-...",
  "templateId":  "tpl_...",
  "documentId":  "b1e2d934-...",
  "documentUrl": "http://localhost:7000/api/documents/b1e2d934-.../download",
  "status":      "completed"
}`}
      </CodeBlock>

      <H2 id="payload-reference">Payload reference</H2>
      <P>
        The payload keys you must supply depend entirely on the variable names you set when
        building the template. The table below shows which section types contribute payload keys
        and what value types they expect.
      </P>
      <PropTable props={[
        { name: 'header.fields[n].variable', type: 'string',   required: false, description: 'Each header field variable → a display string (e.g. invoice number, date).' },
        { name: 'header.recipientVar',       type: 'string',   required: false, description: 'Recipient address block — multiline strings are rendered with line breaks.' },
        { name: 'text section variable',     type: 'string',   required: false, description: 'Single string value. Newlines preserved when multiline is enabled.' },
        { name: 'table section variable',    type: 'object[]', required: false, description: 'Array of row objects. Each object must have a key for every column defined in the template.' },
        { name: 'summary row variable',      type: 'string | number', required: false, description: 'Scalar value for each summary row. Use numbers for currency/number formats.' },
        { name: 'signature nameVar',         type: 'string',   required: false, description: 'Name of the signatory, displayed below the signature line.' },
        { name: 'signature dateVar',         type: 'string',   required: false, description: 'Signing date displayed next to the name.' },
      ]} />
      <Callout type="tip">
        Click <strong>Generate PDF</strong> inside the template editor to open the generate modal.
        The payload textarea is automatically pre-filled with a sample JSON object derived from
        the current template configuration — all variable names are present with sensible example
        values ready to submit or edit.
      </Callout>
    </Article>
  )
}
